import React, { useEffect, useRef, useState } from 'react'
import { money } from '../financial/formatting'
import { compactMoney, count, fmtHour } from './model'

// Below this column width a value label collides with its neighbours', so
// only the tallest bar keeps one.
const MIN_LABEL_COLUMN_PX = 40

// Revenue per New York hour of day, 0-23. by_hour carries every hour, so an
// empty hour is a real zero, not a gap. One series, so no legend: the title
// names it. Labels go above every non-empty bar when they fit and only above
// the tallest one when they don't; the exact figure for any hour is in the
// tooltip, by mouse or keyboard.
export default function RevenueByHourCard({ rows }) {
  const hours = Array.from({ length: 24 }, (_, h) => rows?.find((r) => r.hour === h) || { hour: h, revenue: 0, sold: 0, received: 0 })
  const values = hours.map((r) => Number(r.revenue || 0))
  const max = Math.max(0, ...values)
  const maxHour = values.indexOf(max)

  const barsRef = useRef(null)
  const [columnWidth, setColumnWidth] = useState(0)
  const [active, setActive] = useState(null)

  useEffect(() => {
    const el = barsRef.current
    if (!el) return undefined
    const measure = () => setColumnWidth(el.getBoundingClientRect().width / 24)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [max])

  const labelsFit = columnWidth >= MIN_LABEL_COLUMN_PX
  const showLabel = (hour, value) => value > 0 && (labelsFit || hour === maxHour)

  return (
    <section className="card" aria-labelledby="sl-hours-title" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h2 id="sl-hours-title" style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
        Revenue by hour <span style={{ fontWeight: 500, fontSize: 12, color: 'var(--text-muted)' }}>(ET)</span>
      </h2>
      {max === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>No revenue in this range.</p>
      ) : (
        <div>
          <div ref={barsRef} className="sl-hour-bars" role="list" aria-label="Revenue per hour" style={{ position: 'relative' }}>
            {hours.map((r, i) => {
              const value = values[i]
              return (
                <div
                  key={r.hour}
                  role="listitem"
                  tabIndex={0}
                  aria-label={`${fmtHour(r.hour)}: ${money(value)}`}
                  onMouseEnter={() => setActive(r.hour)}
                  onMouseLeave={() => setActive(null)}
                  onFocus={() => setActive(r.hour)}
                  onBlur={() => setActive(null)}
                  style={{
                    display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center',
                    height: '100%', minWidth: 0, cursor: 'default', outlineOffset: 2,
                    background: active === r.hour ? 'var(--chart-cursor)' : 'transparent', borderRadius: 4,
                  }}
                >
                  {showLabel(r.hour, value) && (
                    <span data-testid="hour-value" style={{ fontSize: 10, color: 'var(--text-secondary)', marginBottom: 2, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {compactMoney(value)}
                    </span>
                  )}
                  <div style={{ width: '100%', height: `${(value / max) * 85}%`, minHeight: value > 0 ? 2 : 0, background: 'var(--accent)', borderRadius: '4px 4px 0 0' }} />
                </div>
              )
            })}
            {active !== null && <HourTooltip row={hours[active]} value={values[active]} barPct={(values[active] / max) * 85} />}
          </div>
          <div aria-hidden="true" style={{ display: 'grid', gridTemplateColumns: 'repeat(24, minmax(0, 1fr))', gap: 2, borderTop: '1px solid var(--border-strong)', paddingTop: 4 }}>
            {hours.map((r) => (
              <span key={r.hour} style={{ fontSize: 9, color: 'var(--text-muted)', textAlign: 'center' }}>
                {r.hour % 3 === 0 ? fmtHour(r.hour) : ''}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

// Sits just above the hovered bar. At the edges it anchors to the bar's
// outer side instead of centring, so it never spills out of the card.
function HourTooltip({ row, value, barPct }) {
  const { hour } = row
  const center = ((hour + 0.5) / 24) * 100
  const placement = hour < 3
    ? { left: `${(hour / 24) * 100}%`, transform: 'none' }
    : hour > 20
      ? { left: `${((hour + 1) / 24) * 100}%`, transform: 'translateX(-100%)' }
      : { left: `${center}%`, transform: 'translateX(-50%)' }

  return (
    <div
      role="tooltip"
      style={{
        position: 'absolute', bottom: `calc(${barPct}% + 18px)`, zIndex: 2, pointerEvents: 'none',
        padding: '6px 9px', borderRadius: 6, whiteSpace: 'nowrap', fontSize: 12,
        background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow)',
        ...placement,
      }}
    >
      <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{fmtHour(hour)} ET</div>
      <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{money(value)}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{count(row.sold)} sold of {count(row.received)}</div>
    </div>
  )
}
